import React, { useState, useEffect, useContext } from 'react';
import { Drawer, message as AntMessage } from 'antd';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import SupportRequests from './tickets';
import { AuthContext } from '../../appContext';
import {
  GET_SUPPORTS, GET_SUPPORT_REPLY, CREATE_SUPPORT_REPLY, GET_USER_SUPPORTS, UPDATE_SUPPORT,
} from '../../graphql';
import CircularProgress from '../../components/CircularProgress';
import Replies from './replies';
import './styles.less';

const Support = () => {
  const { userInfo } = useContext(AuthContext);
  // Administrators see every ticket; an instructor sees the ones they raised.
  const isAdmin = userInfo.role === 'ADMIN';
  const query = isAdmin ? GET_SUPPORTS : GET_USER_SUPPORTS;
  const listKey = isAdmin ? 'supports' : 'mySupports';
  // Ordering is the server's now; `isClosed` defaults to open.
  const queryVariables = isAdmin ? { isClosed: false } : {};

  const { data: supportPayload, loading: supportLoading } = useQuery(query, {
    variables: queryVariables,
  });
  const [updateSupport] = useMutation(UPDATE_SUPPORT);
  const [replySupport] = useMutation(CREATE_SUPPORT_REPLY);
  const [getReplies, { data: repliesPayload, loading: replyLoading }] = useLazyQuery(GET_SUPPORT_REPLY, {
    pollInterval: 1000,
  });
  const [supports, setSupport] = useState([]);
  const [supportReplies, setSupportReplies] = useState([]);
  const [uiControl, setUiControl] = useState({
    loader: false,
    drawerState: false,
    selectedSupportId: null,
    searchInput: null,
    selectedSupport: null,
  });

  useEffect(() => {
    if (!supportLoading && supportPayload) {
      setSupport(supportPayload[listKey]);
    }
  }, [supportPayload, supportLoading, listKey]);

  useEffect(() => {
    if (!replyLoading && repliesPayload) {
      setSupportReplies(repliesPayload.supportReplies);
    }
  }, [repliesPayload, replyLoading]);

  /** Replaces the list in the cache with the result of `update`. */
  const writeList = (cache, update) => {
    const cached = cache.readQuery({ query, variables: queryVariables });
    if (!cached) return;
    cache.writeQuery({
      query,
      variables: queryVariables,
      data: { [listKey]: update(cached[listKey]) },
    });
  };

  const closeSupport = async (id) => {
    try {
      await updateSupport({
        variables: { id, input: { isClosed: true } },
        update: (cache) => {
          writeList(cache, (existing) => existing.filter(({ id: supportId }) => supportId !== id));
        },
      });
      setUiControl({ ...uiControl, selectedSupportId: null, selectedSupport: null });
      AntMessage.success('Support closed successfully');
    } catch (error) {
      AntMessage.error(error.message || 'Error, could not close support');
    }
  };

  const onSelectTicket = (ticket) => {
    setUiControl({ ...uiControl, selectedSupport: ticket, selectedSupportId: ticket.id });
    getReplies({ variables: { supportId: ticket.id } });
  };

  const onReply = (message) => {
    const supportId = uiControl.selectedSupportId;
    // The author comes from the token; the ticket comes from the path.
    replySupport({
      variables: { supportId, input: { message } },
      update: (cache, { data: { createSupportReply: created } }) => {
        const replyVariables = { supportId };
        const cachedReplies = cache.readQuery({
          query: GET_SUPPORT_REPLY,
          variables: replyVariables,
        });
        if (cachedReplies) {
          cache.writeQuery({
            query: GET_SUPPORT_REPLY,
            variables: replyVariables,
            data: { supportReplies: [...cachedReplies.supportReplies, created] },
          });
        }
        writeList(cache, (existing) => existing.map((support) => (
          support.id === supportId
            ? { ...support, replyCount: support.replyCount + 1 }
            : support
        )));
      },
    });
  };

  return (
    <div className="gx-main-content">
      <div className="gx-app-module gx-chat-module">
        <div className="gx-chat-module-box">
          <div className="gx-d-block gx-d-lg-none">
            <Drawer
              placement="left"
              closable={false}
              open={uiControl.drawerState}
              onClose={() => setUiControl({ ...uiControl, drawerState: false })}
            >
              <SupportRequests
                supports={supports}
                selectedTicketId={uiControl.selectedSupportId}
                onSelectTicket={onSelectTicket}
              />
            </Drawer>
          </div>
          <div className="gx-chat-sidenav gx-d-none gx-d-lg-flex">
            <SupportRequests
              supports={supports}
              selectedTicketId={uiControl.selectedSupportId}
              onSelectTicket={onSelectTicket}
            />
          </div>
          {replyLoading
            ? (
              <div className="gx-loader-view">
                <CircularProgress />
              </div>
            ) : (
              <Replies
                replies={supportReplies}
                selectedTicket={uiControl.selectedSupport}
                onReply={onReply}
                closeSupport={closeSupport}
                setDrawerState={(state) => setUiControl({ ...uiControl, drawerState: state })}
              />
            )}
        </div>
      </div>
    </div>
  );
};

export default Support;
